import { utcDayKey } from "../lib/calendarDay";
import { log } from "../core/logger";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import { API_BASE_URL } from "./helpers";
import type { Booking } from "../types/Booking";
import type { CanBookRequest, CanBookValidationResponse } from "../types/CanBookValidation";
import type { CanMoveRequest, CanMoveValidationResponse } from "../types/CanMoveValidation";
import type { PaginatedResponse } from "../types/Pagination";
import type {
  AddRecurrenceRequest,
  AddRecurrenceResponse,
  CheckRecurrenceAvailabilityRequest,
  CheckRecurrenceAvailabilityResponse,
  RemoveRecurrenceRequest,
  RemoveRecurrenceResponse,
} from "../types/Recurrence";
import type { CreateBookingParams, IBookingAPI } from "./bookingApi";

export type RecurrenceExceptionClashDetail = {
  conflictType?: string;
  message?: string;
  conflictingBookingId?: string;
  conflictingMemberId?: string;
  date?: string;
};

export class RecurrenceExceptionError extends Error {
  readonly status: number;
  readonly reason: string;
  readonly detail?: RecurrenceExceptionClashDetail | unknown;

  constructor(status: number, reason: string, detail?: RecurrenceExceptionClashDetail | unknown) {
    super(reason);
    this.name = "RecurrenceExceptionError";
    this.status = status;
    this.reason = reason;
    this.detail = detail;
  }
}

class RemoteBookingAPI implements IBookingAPI {
  clearCache(): void {
    // No caching implemented in remote API
    return;
  }

  async fetchBookingById(id: string): Promise<Booking | null> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/booking/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      if (!response.ok) {
        if (response.status === 404) return null;
        const error = new Error(`API error: ${response.statusText}`) as Error & { status?: number };
        error.status = response.status;
        throw error;
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      log("remoteBookingApi", "Failed to fetch booking", error);
      throw error;
    }
  }

  async fetchSerializedBookings(
    searchTerm?: string,
    skip?: number,
    limit?: number,
    locationId?: string,
    isCancelled?: boolean,
    memberId?: string,
    organisationId?: string,
    type?: "slot" | "adhoc" | "weekend" | "sessional" | "system" | "prospect",
    isRecurring?: boolean,
    isSystemBooking?: boolean,
    specificDate?: Date,
    dateFrom?: Date,
    dateTo?: Date,
    swatch?: string,
  ): Promise<PaginatedResponse<Booking>> {
    try {
      const params = new URLSearchParams();
      // Only add isCancelled param when explicitly set; undefined means fetch all
      if (isCancelled !== undefined) {
        params.append("isCancelled", isCancelled.toString());
      }
      if (searchTerm && searchTerm.trim()) {
        params.append("searchTerm", searchTerm.trim());
      }
      if (skip !== undefined) {
        params.append("skip", skip.toString());
      }
      if (limit !== undefined) {
        params.append("limit", limit.toString());
      }
      if (locationId) {
        params.append("locationId", locationId);
      }
      if (memberId) {
        params.append("memberId", memberId);
      }
      if (organisationId) {
        params.append("organisationId", organisationId);
      }
      // type / isRecurring / isSystemBooking must travel server-side: the API paginates
      // before any client-side filter runs, so a slot-only filter applied locally would
      // return zero rows whenever the first page happens to be all-adhoc.
      if (type) {
        params.append("type", type);
      }
      if (isRecurring !== undefined) {
        params.append("isRecurring", isRecurring.toString());
      }
      if (isSystemBooking !== undefined) {
        params.append("isSystemBooking", isSystemBooking.toString());
      }
      // specificDate ("show me <day>'s bookings") must travel server-side so the day filter
      // narrows the result set *before* pagination — applied client-side it only ever filters
      // the current 50-row page. Sent as yyyy-MM-dd (the single-day convention fetchBookingsForDay
      // uses); the server expands it to a recurrence-aware [startOfDay, endOfDay] window.
      // Uses the UTC calendar day (utcDayKey), not date-fns' local-timezone `format`, so the day
      // sent to the server agrees with occursOnDate and the rest of the UTC-anchored calendar —
      // callers now hand this a UTC-midnight Date (see dayFilterToDate / enumerateOccurrences).
      if (specificDate) {
        params.append("specificDate", utcDayKey(specificDate));
      }
      // dateFrom / dateTo / swatch must travel server-side: the /admin/bookings table is
      // server-paginated, so filtering client-side would only ever narrow the current page.
      // The server's dateFrom/dateTo handling is recurrence-aware; swatch filters by room colour.
      if (dateFrom) {
        params.append("dateFrom", utcDayKey(dateFrom));
      }
      if (dateTo) {
        params.append("dateTo", utcDayKey(dateTo));
      }
      if (swatch) {
        params.append("swatch", swatch);
      }

      const response = await fetchWithAuth(`${API_BASE_URL}/api/bookings?${params.toString()}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }
      const result: PaginatedResponse<Booking> = await response.json();
      return result;
    } catch (error) {
      log("remoteBookingApi", "Failed to fetch bookings", error);
      throw error;
    }
  }

  async fetchBookingsByDateAndMember(date: Date): Promise<Booking[]> {
    try {
      const dateString = utcDayKey(date);
      const response = await fetchWithAuth(`${API_BASE_URL}/api/bookings?date=${dateString}`);

      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }

      const bookings: Booking[] = await response.json();
      return bookings;
    } catch (error) {
      log("remoteBookingApi", "Failed to fetch bookings", error);
      throw error;
    }
  }

  /**
   * Fetches EVERY non-cancelled booking the server matches for a calendar window at a location.
   *
   * GET /api/bookings is paginated (server DEFAULT_LIMIT 200, MAX_LIMIT 1000). The calendar
   * previously sent no limit and ignored `pagination.hasMore`, so a location whose week window
   * matched more than 200 documents silently lost the tail of the page — under the server's
   * `now`-bucketed timeline sort that tail is the oldest-genesis recurring/slot series, i.e. the
   * long-standing regulars that only appear by recurring into the window (Richmond week view:
   * 229 matched, 200 returned, every old slot in Red/Yellow/Blue/Amber vanished while day view,
   * at 168 rows, was complete). The calendar must be a true reflection of the room, so:
   *   - ask for the largest page the server allows (`includeAll=true` → MAX_LIMIT),
   *   - request an explicit stable sort so `skip` paging is deterministic (the default timeline
   *     sort is keyed on request-time `now`, which can move between pages),
   *   - keep paging until the server reports no more rows, de-duplicating by id in case a row
   *     straddles a page boundary because a booking was created mid-fetch.
   */
  private async fetchAllBookingsInWindow(
    dateFrom: Date,
    dateTo: Date,
    locationId: string,
  ): Promise<PaginatedResponse<Booking>> {
    const from = utcDayKey(dateFrom);
    const to = utcDayKey(dateTo);
    const seen = new Set<string>();
    const data: Booking[] = [];
    let skip = 0;
    let total = 0;
    let limit = 0;
    // Hard ceiling on round-trips so a misbehaving server can never spin the calendar forever.
    const MAX_PAGES = 20;

    for (let pageIndex = 0; pageIndex < MAX_PAGES; pageIndex++) {
      const params = new URLSearchParams({
        dateFrom: from,
        dateTo: to,
        locationId,
        isCancelled: "false",
        includeAll: "true",
        sortField: "date",
        sortOrder: "asc",
        skip: skip.toString(),
      });
      const response = await fetchWithAuth(`${API_BASE_URL}/api/bookings?${params.toString()}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }
      const page: PaginatedResponse<Booking> = await response.json();
      const rows = page.data ?? [];
      for (const booking of rows) {
        if (seen.has(booking.id)) continue;
        seen.add(booking.id);
        data.push(booking);
      }
      total = page.pagination?.total ?? data.length;
      limit = page.pagination?.limit ?? rows.length;
      skip += rows.length;
      // Stop on the server's word, or if it returned nothing (guards against a hasMore that
      // never clears — better a stable partial than an infinite loop).
      if (!page.pagination?.hasMore || rows.length === 0) break;
    }

    return {
      success: true,
      data,
      pagination: { total, limit, skip: 0, hasMore: false, returned: data.length },
    };
  }

  async fetchBookingsForDay(date: Date, locationId: string): Promise<PaginatedResponse<Booking>> {
    try {
      // Same date for both bounds — the backend treats the range as inclusive.
      return await this.fetchAllBookingsInWindow(date, date, locationId);
    } catch (error) {
      log("remoteBookingApi", "Failed to fetch bookings for day", error);
      throw error;
    }
  }

  async fetchBookingsForWeek(date: Date, locationId: string): Promise<PaginatedResponse<Booking>> {
    try {
      const dateTo = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + 7));
      return await this.fetchAllBookingsInWindow(date, dateTo, locationId);
    } catch (error) {
      log("remoteBookingApi", "Failed to fetch bookings", error);
      throw error;
    }
  }

  async fetchBookingsForMonth(date: Date, locationId: string): Promise<PaginatedResponse<Booking>> {
    try {
      const dateTo = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + 30));
      return await this.fetchAllBookingsInWindow(date, dateTo, locationId);
    } catch (error) {
      log("remoteBookingApi", "Failed to fetch bookings", error);
      throw error;
    }
  }

  async fetchBookingsByLocation(locationId: string): Promise<Booking[]> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/bookings?locationId=${locationId}&isCancelled=false`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }

      const json = await response.json();
      // we're ignoring the pagination here...
      const bookings: Booking[] = json.data;
      return bookings;
    } catch (error) {
      log("remoteBookingApi", "Failed to fetch bookings", error);
      throw error;
    }
  }

  // this call is secure because getSerializedBookings only gets bookings for the authenticated member
  // a refactor would be to remove the memberId from the parameters here
  async fetchBookingsByLocationForMember(locationId: string, memberId: string): Promise<Booking[]> {
    const response = await this.fetchSerializedBookings();
    return response.data.filter((booking) => booking.locationId === locationId && booking.memberId === memberId);
  }

  // this call is secure because fetchSerializedBookings only gets bookings for the authenticated member
  // a refactor would be to remove the memberId from the parameters here
  async fetchBookingByIdForMember(bookingId: string, memberId: string): Promise<Booking | null> {
    const response = await this.fetchSerializedBookings();
    const booking = response.data.find((b) => b.id === bookingId && b.memberId === memberId) || null;
    return booking;
  }

  async fetchWeeklyBookingsForRoom(
    roomId: string,
    date: Date,
    numberOfWeeks: number,
    locationId?: string,
  ): Promise<Booking[]> {
    try {
      // Use dateFrom/dateTo so the server's recurrence-aware filter actually fires —
      // sending `date`/`swing`/`back` is a no-op the server silently ignored, which
      // capped the response at the default limit in date-DESC order. For a room with
      // recurring bookings whose genesis is months in the past, that meant the upcoming
      // occurrences vanished from the result entirely. See bookingRoutes.ts dateFrom/
      // dateTo handling at the GET /bookings handler.
      //
      // ALSO include locationId. Without it, the server's role gate restricts the
      // response to the caller's own bookings (BookingRepository.getBookings — the
      // "Non-elevated users: enforce their own memberId unless viewing a location
      // calendar" branch). Conflict checks need to see OTHER members' bookings in the
      // room too, otherwise the +30 button has nothing to clash against. Sending
      // locationId puts the request on the calendar-view path, returning every
      // booking at that location → roomId then narrows it back down on the server.
      const dateFrom = utcDayKey(date);
      const dateToObj = new Date(
        Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + numberOfWeeks * 7),
      );
      const dateTo = utcDayKey(dateToObj);

      const locationParam = locationId ? `&locationId=${encodeURIComponent(locationId)}` : "";
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/bookings?dateFrom=${dateFrom}&dateTo=${dateTo}&roomId=${encodeURIComponent(roomId)}${locationParam}&isCancelled=false`,
      );
      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }
      const result = await response.json();
      // Handle both direct array and wrapped response formats
      const bookings: Booking[] = Array.isArray(result) ? result : (result.data || []);
      return bookings;
    } catch (error) {
      log("remoteBookingApi", "Failed to fetch bookings", error);
      throw error;
    }
  }

  async fetchBookingCountsByLocation(): Promise<Record<string, number>> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/bookings/count-by-location`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      log("remoteBookingApi", "Failed to fetch booking counts by location", error);
      throw error;
    }
  }

  async addNewBooking(params: CreateBookingParams): Promise<Booking> {
    try {
      // Prepare the request body
      const body = {
        locationId: params.locationId,
        roomId: params.roomId,
        memberId: params.memberId,
        // UTC calendar day — `params.date` is UTC-midnight of the centre's day; date-fns `format`
        // here reinterpreted it in the viewer's LOCAL tz, creating the booking on the previous day
        // for viewers west of UTC. utcDayKey keeps it viewer-timezone independent (matches reads).
        date: utcDayKey(params.date),
        startTime: params.startTime,
        endTime: params.endTime,
        type: params.type,
        hasBuffers: params.hasBuffers ?? true,
        isRecurring: params.isRecurring ?? false,
        recurrenceCount: params.recurrenceCount,
        recurrenceOccurrences: params.recurrenceOccurrences,
        recurrenceFrequency: params.recurrenceFrequency,
        recurrenceInterval: params.recurrenceInterval,
        recurrenceUntil: params.recurrenceUntil,
        amenityIds: params.amenityIds,
        memberNotes: params.memberNotes,
        title: params.title, // Optional booking title (e.g., "Fish Corp - Harrison" for org owner bookings)
        // Manual billing tag for group sessions (no behaviour gated on it). Must be forwarded here —
        // omitting it silently dropped the "make group booking" toggle at creation.
        isGroupBooking: params.isGroupBooking ?? false,
        // R24 pull-forward — admin-chosen slot start (YYYY-MM-DD); server validates + uses as genesis.
        slotStartOverride: params.slotStartOverride,
        // Elevated past-date waive-billing flag (Task 7). Defaults false if omitted.
        waiveBilling: params.waiveBilling ?? false,
        // Elevated cross-member buffer override (Task 10). Defaults false — omitting it would
        // silently drop a confirmed "Book anyway" retry and re-produce the same 409 forever.
        overrideBufferConflict: params.overrideBufferConflict ?? false,
      };

      const response = await fetchWithAuth(`${API_BASE_URL}/api/bookings`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include", // Include cookies for authentication
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        // Parse error response for detailed validation messages
        const errorData = await response.json().catch(() => ({ error: response.statusText }));

        // Check if it's a conflict error with detailed information
        if (response.status === 409 && errorData.details) {
          const error = new Error(errorData.details);
          (error as any).conflictType = errorData.conflictType;
          (error as any).conflictingBooking = errorData.conflictingBooking;
          throw error;
        }

        // For other errors, throw with the error details
        throw new Error(errorData.details || errorData.error || `API error: ${response.statusText}`);
      }

      const result = await response.json();
      const booking: Booking = result.data || result;
      log("remoteBookingApi", "addNewBooking", "Created booking:", booking);
      return booking;
    } catch (error) {
      log("remoteBookingApi", "Failed to create booking", error);
      throw error;
    }
  }

  async checkCanBook(params: CanBookRequest): Promise<CanBookValidationResponse> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/can-book`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }

      const result: CanBookValidationResponse = await response.json();
      return result;
    } catch (error) {
      log("remoteBookingApi", "Failed to check booking availability", error);
      throw error;
    }
  }

  async checkCanMove(params: CanMoveRequest): Promise<CanMoveValidationResponse> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/can-move`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }

      const result: CanMoveValidationResponse = await response.json();
      return result;
    } catch (error) {
      log("remoteBookingApi", "Failed to check if booking can be moved", error);
      throw error;
    }
  }

  async updateBookingDateTime(bookingId: string, newDate: string, newStartTime: string, newEndTime: string, waive?: boolean): Promise<Booking> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/booking/${bookingId}/datetime`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ newDate, newStartTime, newEndTime, ...(waive ? { waive: true } : {}) }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }));
        // Prefer the human-readable `details` over the generic `error` code ("Forbidden") so the user
        // sees WHY (e.g. the R20 "speak to a practice manager to move to a different day" explanation)
        // instead of a bare "Forbidden".
        throw new Error(errorData.details || errorData.error || `Failed to update booking: ${response.statusText}`);
      }

      const result = await response.json();
      return result.booking;
    } catch (error) {
      log("remoteBookingApi", "Failed to update booking date/time", error);
      throw error;
    }
  }

  async updateBookingRoom(bookingId: string, newRoomId: string): Promise<Booking> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/booking/${bookingId}/room`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ newRoomId }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(errorData.error || errorData.details || `Failed to update booking room: ${response.statusText}`);
      }

      const result = await response.json();
      return result.booking;
    } catch (error) {
      log("remoteBookingApi", "Failed to update booking room", error);
      throw error;
    }
  }

  async updateBookingMember(bookingId: string, newMemberId: string): Promise<Booking> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/booking/${bookingId}/member`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ newMemberId }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(errorData.error || errorData.details || `Failed to assign booking to member: ${response.statusText}`);
      }

      const result = await response.json();
      return result.booking;
    } catch (error) {
      log("remoteBookingApi", "Failed to assign booking to member", error);
      throw error;
    }
  }

  async updateNotesOnBooking(bookingId: string, notes: string): Promise<Booking | null> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/booking/${bookingId}/notes`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ notes }),
      });
      if (!response.ok) {
        if (response.status === 404) return null;
        throw new Error(`API error: ${response.statusText}`);
      }
      const result = await response.json();
      return result.data;
    } catch (error) {
      log("remoteBookingApi", "Failed to update booking notes", error);
      throw error;
    }
  }

  async cancelBooking(bookingId: string, opts?: { waive?: boolean; waiveNote?: string }): Promise<Booking> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/booking/${bookingId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ waive: opts?.waive ?? false, waiveNote: opts?.waiveNote }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }));
        // Prefer the specific `details` (e.g. C5 "already finished") over the generic `error`
        // ("Validation failed") so the dialog shows the actual reason.
        throw new Error(errorData.details || errorData.error || `Failed to cancel booking: ${response.statusText}`);
      }

      const result = await response.json();
      return result.booking;
    } catch (error) {
      log("remoteBookingApi", "Failed to cancel booking", error);
      throw error;
    }
  }

  async upgradeFlexToSlot(bookingId: string, slotStartOverride?: string): Promise<Booking> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/booking/${bookingId}/upgrade-to-slot`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(slotStartOverride ? { slotStartOverride } : {}),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }));
        // Surface the server's `reason` so the hook can map it to a user-facing message.
        throw new Error(errorData.reason ?? errorData.error ?? `Failed to upgrade booking: ${response.statusText}`);
      }

      const result = await response.json();
      return result.booking;
    } catch (error) {
      log("remoteBookingApi", "Failed to upgrade flex booking to slot", error);
      throw error;
    }
  }

  async getUpgradeToSlotPreview(bookingId: string): Promise<import("./bookingApi").UpgradeToSlotPreview> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/booking/${bookingId}/upgrade-to-slot/preview`, {
      method: "GET",
      credentials: "include",
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(errorData.error ?? `Failed to load upgrade preview: ${response.statusText}`);
    }
    return response.json();
  }

  async getOccurrenceHistory(bookingId: string): Promise<import("../types/BookingOccurrenceHistory").BookingOccurrenceHistory> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/booking/${bookingId}/occurrence-history`, {
      method: "GET",
      credentials: "include",
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(errorData.error ?? `Failed to load occurrence history: ${response.statusText}`);
    }
    return response.json();
  }

  async addSlotHoliday(
    bookingId: string,
    dates: string[]
  ): Promise<{
    success: boolean;
    booking: Booking;
    holidayDates: string[];
    totalHoursPaused: number;
    formattedDates: string[];
  }> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/slot-bookings/${bookingId}/add-holiday`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ dates }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `API error: ${response.statusText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      log("remoteBookingApi", "Failed to add slot holiday", error);
      throw error;
    }
  }

  async setRecurrenceException(
    bookingId: string,
    date: string,
    action: "add" | "remove",
    waive?: boolean,
  ): Promise<{
    success: boolean;
    booking: Booking;
    action: "add" | "remove";
    date: string;
    exceptions: string[];
    wasChargedOnCancel?: boolean;
  }> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/booking/${bookingId}/recurrence/exception`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ date, action, ...(waive ? { waive: true } : {}) }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }));
        throw new RecurrenceExceptionError(response.status, errorData?.error ?? "unknown", errorData?.detail);
      }

      return await response.json();
    } catch (error) {
      log("remoteBookingApi", "Failed to set recurrence exception", error);
      throw error;
    }
  }

  async checkRecurrenceAvailability(
    bookingId: string,
    params: CheckRecurrenceAvailabilityRequest,
  ): Promise<CheckRecurrenceAvailabilityResponse> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/booking/${bookingId}/check-recurrence-availability`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(errorData.error || errorData.details || `API error: ${response.statusText}`);
      }

      const result: CheckRecurrenceAvailabilityResponse = await response.json();
      return result;
    } catch (error) {
      log("remoteBookingApi", "Failed to check recurrence availability", error);
      throw error;
    }
  }

  async addRecurrence(bookingId: string, params: AddRecurrenceRequest): Promise<AddRecurrenceResponse> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/booking/${bookingId}/recurrence`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(errorData.error || errorData.details || `Failed to add recurrence: ${response.statusText}`);
      }

      const result: AddRecurrenceResponse = await response.json();
      return result;
    } catch (error) {
      log("remoteBookingApi", "Failed to add recurrence to booking", error);
      throw error;
    }
  }

  async removeRecurrence(bookingId: string, params: RemoveRecurrenceRequest): Promise<RemoveRecurrenceResponse> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/booking/${bookingId}/recurrence`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(errorData.error || errorData.details || `Failed to remove recurrence: ${response.statusText}`);
      }

      const result: RemoveRecurrenceResponse = await response.json();
      return result;
    } catch (error) {
      log("remoteBookingApi", "Failed to remove recurrence from booking", error);
      throw error;
    }
  }

  async cancelBookingFromDate(
    bookingId: string,
    fromDate: string,
    opts?: { waive?: boolean; waiveNote?: string },
  ): Promise<{ success: boolean; cancelledCount: number; wasChargedOnCancel: boolean; message: string }> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/booking/${bookingId}/cancel-from`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ fromDate, waive: opts?.waive ?? false, waiveNote: opts?.waiveNote }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(errorData.error || errorData.details || `Failed to cancel from date: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      log("remoteBookingApi", "Failed to cancel booking from date", error);
      throw error;
    }
  }
}

export default RemoteBookingAPI;
